import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useRef } from "react";
import { SONGS } from "@/data/eiffel65";

const PlayerCtx = createContext(null);

// === Lazy-load the YouTube IFrame API exactly once ===
let _ytApiPromise = null;
function loadYouTubeAPI() {
  if (_ytApiPromise) return _ytApiPromise;
  _ytApiPromise = new Promise((resolve) => {
    if (window.YT && window.YT.Player) return resolve(window.YT);
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    tag.async = true;
    document.head.appendChild(tag);
    window.onYouTubeIframeAPIReady = () => resolve(window.YT);
  });
  return _ytApiPromise;
}

export const PlayerProvider = ({ children }) => {
  const [currentSongId, setCurrentSongId] = useState(null);
  const [queue, setQueue] = useState([]); // list of song ids — the "playlist"
  const [shuffle, setShuffle] = useState(false);
  const [repeat, setRepeat] = useState(false);

  // Live YT player state
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolumeState] = useState(75);
  const [muted, setMuted] = useState(false);
  const [ready, setReady] = useState(false);

  // Autoplay policy: never start playback before the user explicitly clicks
  // Play on this site. Once they have, subsequent videos in the queue
  // auto-advance via onEnded → next(). This is required to comply with
  // browser autoplay policies and the project's no-autoplay UX requirement.
  const hasInteractedRef = useRef(false);

  const playerRef = useRef(null);     // YT player instance
  const containerRef = useRef(null);  // the offscreen div that YT replaces
  const pollRef = useRef(null);

  const currentSong = useMemo(
    () => SONGS.find((s) => s.id === currentSongId) || null,
    [currentSongId]
  );

  // === Boot the YT player exactly once ===
  useEffect(() => {
    let alive = true;
    loadYouTubeAPI().then((YT) => {
      if (!alive || playerRef.current || !containerRef.current) return;
      playerRef.current = new YT.Player(containerRef.current, {
        height: "100%",
        width: "100%",
        videoId: "",
        playerVars: {
          autoplay: 0,
          controls: 0,
          modestbranding: 1,
          rel: 0,
          playsinline: 1,
          enablejsapi: 1,
          origin: window.location.origin,
        },
        events: {
          onReady: (e) => {
            setReady(true);
            try { e.target.setVolume(volume); } catch { /* noop */ }
          },
          onStateChange: (e) => {
            const s = e.data;
            // 1=playing, 2=paused, 0=ended, 3=buffering, 5=cued
            if (s === 1) setIsPlaying(true);
            else if (s === 2) setIsPlaying(false);
            else if (s === 0) handleEnded();
          },
          onError: () => {
            // Skip on error (e.g. embedding disabled for a video)
            handleEnded();
          },
        },
      });
    });
    return () => { alive = false; };
  }, []);

  // === Poll current time/duration while playing ===
  useEffect(() => {
    clearInterval(pollRef.current);
    if (!isPlaying || !playerRef.current) return;
    pollRef.current = setInterval(() => {
      try {
        const p = playerRef.current;
        if (!p || typeof p.getCurrentTime !== "function") return;
        setCurrentTime(p.getCurrentTime() || 0);
        const d = p.getDuration ? p.getDuration() : 0;
        if (d && d !== duration) setDuration(d);
      } catch { /* noop */ }
    }, 500);
    return () => clearInterval(pollRef.current);
  }, [isPlaying, duration]);

  // === Load a video into the player when currentSongId changes ===
  useEffect(() => {
    if (!ready || !playerRef.current || !currentSong) return;
    const ytId = currentSong.youtubeId || "68ugkg9RePc";
    try {
      // Before the user has explicitly interacted, only CUE the video (load
      // metadata + thumbnail) — never auto-start. After the first user-driven
      // play(), all subsequent track changes load+play normally so the
      // playlist auto-advances.
      if (hasInteractedRef.current) {
        playerRef.current.loadVideoById({ videoId: ytId, startSeconds: 0 });
        setIsPlaying(true);
      } else {
        playerRef.current.cueVideoById({ videoId: ytId, startSeconds: 0 });
        setIsPlaying(false);
      }
      setCurrentTime(0);
    } catch { /* noop */ }
  }, [currentSong, ready]);

  // === Public actions ===
  const playSong = useCallback((id, list = null) => {
    hasInteractedRef.current = true;
    setCurrentSongId(id);
    if (list && list.length) setQueue(list);
  }, []);

  const playSongs = useCallback((ids, startIndex = 0) => {
    if (!ids || !ids.length) return;
    hasInteractedRef.current = true;
    setQueue(ids);
    setCurrentSongId(ids[Math.max(0, Math.min(startIndex, ids.length - 1))]);
  }, []);

  // Variant of playSongs that only CUES the queue without auto-playing.
  // Used by Radio/Discography pages to preload a playlist on mount.
  const queueSongs = useCallback((ids, startIndex = 0) => {
    if (!ids || !ids.length) return;
    setQueue(ids);
    // Set the current song without marking interaction → cueVideoById path
    setCurrentSongId(ids[Math.max(0, Math.min(startIndex, ids.length - 1))]);
  }, []);

  const togglePlay = useCallback(() => {
    if (!playerRef.current) return;
    if (isPlaying) {
      try { playerRef.current.pauseVideo(); } catch { /* noop */ }
    } else {
      hasInteractedRef.current = true;
      try { playerRef.current.playVideo(); } catch { /* noop */ }
    }
  }, [isPlaying]);

  const pause = useCallback(() => {
    try { playerRef.current?.pauseVideo(); } catch { /* noop */ }
  }, []);

  const play = useCallback(() => {
    hasInteractedRef.current = true;
    try { playerRef.current?.playVideo(); } catch { /* noop */ }
  }, []);

  const seek = useCallback((seconds) => {
    try {
      playerRef.current?.seekTo(seconds, true);
      setCurrentTime(seconds);
    } catch { /* noop */ }
  }, []);

  const setVolume = useCallback((v) => {
    const vv = Math.max(0, Math.min(100, Math.round(v)));
    setVolumeState(vv);
    try { playerRef.current?.setVolume(vv); } catch { /* noop */ }
    if (vv > 0 && muted) {
      setMuted(false);
      try { playerRef.current?.unMute(); } catch { /* noop */ }
    }
  }, [muted]);

  const toggleMute = useCallback(() => {
    setMuted((m) => {
      const nm = !m;
      try {
        if (nm) playerRef.current?.mute();
        else playerRef.current?.unMute();
      } catch { /* noop */ }
      return nm;
    });
  }, []);

  // === Navigation: prev/next walks `queue` if present, otherwise full SONGS ===
  const playable = useMemo(() => {
    const list = (queue && queue.length) ? queue.map((id) => SONGS.find((s) => s.id === id)).filter(Boolean)
                                          : SONGS;
    return list.filter((s) => s.youtubeId);
  }, [queue]);

  const next = useCallback(() => {
    if (!playable.length) return;
    if (!currentSongId) return setCurrentSongId(playable[0].id);
    if (shuffle) {
      const r = playable[Math.floor(Math.random() * playable.length)];
      return setCurrentSongId(r.id);
    }
    const i = playable.findIndex((s) => s.id === currentSongId);
    const nx = playable[(i + 1) % playable.length];
    if (nx) setCurrentSongId(nx.id);
  }, [currentSongId, shuffle, playable]);

  const prev = useCallback(() => {
    if (!playable.length) return;
    if (!currentSongId) return setCurrentSongId(playable[0].id);
    // If we're more than 3s in, restart the song first (Spotify-style).
    if (currentTime > 3) return seek(0);
    const i = playable.findIndex((s) => s.id === currentSongId);
    const pv = playable[(i - 1 + playable.length) % playable.length];
    if (pv) setCurrentSongId(pv.id);
  }, [currentSongId, currentTime, playable, seek]);

  const stop = useCallback(() => {
    try { playerRef.current?.stopVideo(); } catch { /* noop */ }
    setCurrentSongId(null);
    setIsPlaying(false);
    setCurrentTime(0);
    setDuration(0);
  }, []);

  function handleEnded() {
    if (repeat) {
      try { playerRef.current?.seekTo(0, true); playerRef.current?.playVideo(); } catch { /* noop */ }
      return;
    }
    next();
  }

  const value = {
    // current
    currentSong,
    currentSongId,
    queue,
    // playback state
    isPlaying,
    currentTime,
    duration,
    volume,
    muted,
    ready,
    // toggles
    shuffle,
    setShuffle,
    repeat,
    setRepeat,
    // actions
    playSong,
    playSongs,
    queueSongs,
    togglePlay,
    play,
    pause,
    seek,
    next,
    prev,
    stop,
    setVolume,
    toggleMute,
  };

  return (
    <PlayerCtx.Provider value={value}>
      {children}
      {/* Hidden but mounted YT iframe — positioned offscreen but with non-zero size
          (YouTube refuses to play in a 0×0 player). */}
      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-9999px",
          top: 0,
          width: "1px",
          height: "1px",
          opacity: 0,
          pointerEvents: "none",
          overflow: "hidden",
        }}
      >
        <div ref={containerRef} />
      </div>
    </PlayerCtx.Provider>
  );
};

export const usePlayer = () => {
  const ctx = useContext(PlayerCtx);
  if (!ctx) throw new Error("usePlayer must be used inside PlayerProvider");
  return ctx;
};
